import { ApolloError } from '@apollo/client';

export const DEFAULT_ERROR_FALLBACK =
  'Something went wrong on our end. Try again, and contact support if it keeps happening.';

const NETWORK_ERROR =
  "Couldn't reach Farmshare. Check your connection and try again.";

/**
 * Messages that come from code, not from a person writing for the user:
 * Mongo/Mongoose internals, JS crashes, GraphQL validation. Showing these
 * tells the user nothing they can act on.
 */
const INTERNAL_PATTERNS = [
  /Cast to \w+ failed/i,
  /E11000/,
  /ObjectId/,
  /Cannot read propert/i,
  /is not a function/i,
  /(?:undefined|null) is not/i,
  /Variable "\$/,
  /Unexpected token/i,
  /Response not successful/i,
  /Field "[^"]+" (?:of|is not)/,
  /validation failed/i,
  /timed? ?out/i,
];

function isNetworkFailure(message: string) {
  // Word-bounded: "Download failed" contains "load failed" but isn't a
  // network failure (Safari's fetch error is exactly "Load failed").
  return /\bFailed to fetch\b|\bNetworkError\b|\bLoad failed\b|\bNetwork request failed\b/i.test(
    message,
  );
}

/**
 * An Apollo network error with an HTTP status (a `ServerError` /
 * `ServerParseError`) means the server was reached and answered with an
 * error, so it isn't a connection problem: use the server's own message from
 * the response body when there is one, else the fallback. Only a request
 * that never got a response is "couldn't reach Farmshare".
 */
function fromNetworkError(networkError: Error, fallback: string): string {
  const { statusCode, result } = networkError as Error & {
    statusCode?: number;
    result?: { errors?: Array<{ message?: string }> } | string;
  };
  if (typeof statusCode !== 'number') return NETWORK_ERROR;
  const serverMessage =
    result && typeof result === 'object'
      ? result.errors?.[0]?.message
      : undefined;
  return serverMessage ? userFacingError(serverMessage, fallback) : fallback;
}

/**
 * The message to show a user for a caught error: the server's own wording
 * when it was written for people (e.g. "This invoice is not in Draft
 * status"), stripped of "ApolloError:"/"Error:" prefixes — otherwise the
 * `fallback`, which should say what failed in the user's terms.
 */
export function userFacingError(
  error: unknown,
  fallback: string = DEFAULT_ERROR_FALLBACK,
): string {
  if (error instanceof ApolloError) {
    if (error.networkError)
      return fromNetworkError(error.networkError, fallback);
    const first = error.graphQLErrors?.[0]?.message;
    return first ? userFacingError(first, fallback) : fallback;
  }

  let raw = '';
  if (typeof error === 'string') raw = error;
  else if (error instanceof Error) raw = error.message;
  const message = raw.replace(/^(?:\w*Error:\s*)+/, '').trim();

  if (!message) return fallback;
  if (isNetworkFailure(message)) return NETWORK_ERROR;
  if (INTERNAL_PATTERNS.some((re) => re.test(message))) return fallback;
  return message;
}
